import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CustomAlert from '../CustomAlert';

const apiUrl = process.env.REACT_APP_API_URL;

const EMPTY = {
  name: '',
  address: '',
  email: '',
  phone: '',
  website: '',
  bank: '',
  bank_number: '',
  primary_person: '',
  secondary_person: '',
  tax_id: '',
  bussiness_id: '',
  description: '',
};

/**
 * Partner edit form. Same shape as AddPartner: no page chrome, so it sits
 * either in the Drawer on the list page or standalone on /partners/edit/:id.
 *
 * id — from props in the drawer, from the route param standalone
 * onSuccess(updated) — drawer closes and the list refreshes
 */
export default function PartnerEdit({ id: idProp, onSuccess }) {
  const params = useParams();
  const id = idProp ?? params.id;
  const navigate = useNavigate();

  const [partner, setPartner] = useState(EMPTY);
  const [showMore, setShowMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    const token = localStorage.getItem('token');

    fetch(`${apiUrl}/partners/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch partner');
        return res.json();
      })
      // NULL columns would flip the inputs to uncontrolled — keep '' for those.
      .then((data) => setPartner({ ...EMPTY, ...nonNull(data) }))
      .catch((err) => {
        console.error(err);
        setAlert({ show: true, message: 'Неуспешно вчитување на партнерот', type: 'error' });
      });
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPartner((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { name, address, phone } = partner;

    if (!name || !address || !phone) {
      setAlert({ show: true, message: 'Пополнете ги сите задолжителни полиња.', type: 'error' });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/partners/edit/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(partner),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to update partner');

      if (onSuccess) {
        onSuccess(data);
      } else {
        navigate('/partners/list');
      }
    } catch (error) {
      console.error('Error:', error.message);
      setAlert({ show: true, message: error.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Same field spec as AddPartner — the add and edit drawers sit on the same
  // list page, so they should not look like different applications. Plain
  // functions, NOT components: a component declared inside render would
  // remount the input on every keystroke and drop focus.
  const LABEL = 'block text-sm font-medium text-gray-700';
  const INPUT =
    'mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-green-600';

  const renderField = ({ label, name, type = 'text', required = false, span }) => (
    <div key={name} className={span}>
      <label className={LABEL}>
        {label}
        {required && ' *'}
      </label>
      <input
        type={type}
        name={name}
        value={partner[name]}
        onChange={handleChange}
        required={required}
        className={INPUT}
      />
    </div>
  );

  return (
    <>
      <CustomAlert
        isOpen={alert.show}
        type={alert.type}
        title={alert.type === 'success' ? 'Успешно' : 'Грешка'}
        message={alert.message}
        onClose={() => setAlert({ show: false, message: '', type: 'success' })}
      />
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Required fields */}
        <div className="border-l-4 border-green-500 pl-3">
          <h2 className="text-base font-semibold text-gray-800">Основни податоци</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
          {renderField({ label: 'Назив на партнер', name: 'name', required: true, span: 'sm:col-span-2 lg:col-span-3' })}
          {renderField({ label: 'Адреса', name: 'address', required: true, span: 'lg:col-span-2' })}
          {renderField({ label: 'Телефон', name: 'phone', required: true })}
        </div>

        {/* Optional fields */}
        <div className="border-t border-gray-200 pt-4">
          <button
            type="button"
            onClick={() => setShowMore(!showMore)}
            className="flex items-center text-sm text-blue-600 hover:text-blue-800 font-semibold focus:outline-none transition-colors"
          >
            <span className="mr-2">{showMore ? '▼' : '▶'}</span>
            {showMore ? 'Сокриј дополнителни полиња' : 'Прикажи дополнителни полиња'}
          </button>

          {showMore && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3 mt-3">
              {renderField({ label: 'Даночен број', name: 'tax_id' })}
              {renderField({ label: 'Матичен број', name: 'bussiness_id' })}
              {renderField({ label: 'Е-пошта', name: 'email', type: 'email' })}
              {renderField({ label: 'Веб-страна', name: 'website' })}
              {renderField({ label: 'Банка', name: 'bank' })}
              {renderField({ label: 'Жиро сметка', name: 'bank_number' })}
              {renderField({ label: 'Прв контакт', name: 'primary_person' })}
              {renderField({ label: 'Втор контакт', name: 'secondary_person' })}

              <div className="sm:col-span-2 lg:col-span-3">
                <label className={LABEL}>Опис</label>
                <textarea
                  name="description"
                  value={partner.description}
                  onChange={handleChange}
                  rows={2}
                  className={INPUT}
                />
              </div>
            </div>
          )}
        </div>

        <div className="pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={saving}
            className="w-full px-4 py-2.5 bg-green-500 hover:bg-green-600 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm transition-colors"
          >
            {saving ? 'Се зачувува...' : 'Зачувај промени'}
          </button>
        </div>
      </form>
    </>
  );
}

function nonNull(row) {
  return Object.fromEntries(Object.entries(row).filter(([, v]) => v !== null));
}
